/**
 * Timezone Settings Component
 * Allows users to select their preferred timezone
 */

import { useState, useEffect } from 'react';
import { Button, Modal } from '../common';
import { Globe, Check, Clock } from 'lucide-react';

interface TimezoneSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
  region: string;
}

// Common timezones organized by region
const TIMEZONE_OPTIONS: TimezoneOption[] = [
  // Auto-detect option
  { value: 'auto', label: 'Auto-detect from browser', offset: '', region: 'Auto' },
  
  // North America
  { value: 'America/New_York', label: 'Eastern Time (New York)', offset: 'UTC-5/-4', region: 'North America' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)', offset: 'UTC-6/-5', region: 'North America' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)', offset: 'UTC-7/-6', region: 'North America' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)', offset: 'UTC-8/-7', region: 'North America' },
  { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)', offset: 'UTC-9/-8', region: 'North America' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)', offset: 'UTC-10', region: 'North America' },
  
  // Europe
  { value: 'Europe/London', label: 'Greenwich Mean Time (London)', offset: 'UTC+0/+1', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European Time (Paris)', offset: 'UTC+1/+2', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Central European Time (Berlin)', offset: 'UTC+1/+2', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Central European Time (Rome)', offset: 'UTC+1/+2', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Central European Time (Madrid)', offset: 'UTC+1/+2', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Central European Time (Amsterdam)', offset: 'UTC+1/+2', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Central European Time (Stockholm)', offset: 'UTC+1/+2', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow Standard Time', offset: 'UTC+3', region: 'Europe' },
  
  // Asia
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)', offset: 'UTC+4', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (Mumbai)', offset: 'UTC+5:30', region: 'Asia' },
  { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (Dhaka)', offset: 'UTC+6', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok)', offset: 'UTC+7', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore Standard Time', offset: 'UTC+8', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time', offset: 'UTC+8', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)', offset: 'UTC+8', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)', offset: 'UTC+9', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)', offset: 'UTC+9', region: 'Asia' },
  
  // Australia & Oceania
  { value: 'Australia/Perth', label: 'Australian Western Time (Perth)', offset: 'UTC+8', region: 'Australia' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time (Adelaide)', offset: 'UTC+9:30/+10:30', region: 'Australia' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)', offset: 'UTC+10/+11', region: 'Australia' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)', offset: 'UTC+10/+11', region: 'Australia' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (Auckland)', offset: 'UTC+12/+13', region: 'Australia' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Eastern European Time (Cairo)', offset: 'UTC+2', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time', offset: 'UTC+2', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'West Africa Time (Lagos)', offset: 'UTC+1', region: 'Africa' },
  
  // South America
  { value: 'America/Sao_Paulo', label: 'Brasília Time (São Paulo)', offset: 'UTC-3', region: 'South America' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time (Buenos Aires)', offset: 'UTC-3', region: 'South America' },
  { value: 'America/Santiago', label: 'Chile Standard Time (Santiago)', offset: 'UTC-4/-3', region: 'South America' },
];

export function TimezoneSettings({ isOpen, onClose, onSuccess }: TimezoneSettingsProps) {
  const [selectedTimezone, setSelectedTimezone] = useState<string>('auto');
  const [currentTimezone, setCurrentTimezone] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Load current timezone preference
      const saved = localStorage.getItem('user_timezone_preference');
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      setCurrentTimezone(detected);
      setSelectedTimezone(saved || 'auto');
    }
  }, [isOpen]);

  const handleSave = async () => {
    setIsLoading(true);
    
    try {
      // Save to localStorage
      if (selectedTimezone === 'auto') {
        localStorage.removeItem('user_timezone_preference');
      } else {
        localStorage.setItem('user_timezone_preference', selectedTimezone);
      }
      
      // TODO: Save to backend user preferences
      // await userAPI.updatePreferences({ timezone: selectedTimezone });
      
      onSuccess();
    } catch (error) {
      console.error('Failed to save timezone preference:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getEffectiveTimezone = () => {
    return selectedTimezone === 'auto' ? currentTimezone : selectedTimezone;
  };

  const getCurrentTime = (timezone: string) => {
    try {
      return new Date().toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return 'Invalid timezone';
    }
  };

  const filteredOptions = TIMEZONE_OPTIONS.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.region.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedOptions = filteredOptions.reduce((groups, option) => {
    const region = option.region;
    if (!groups[region]) {
      groups[region] = [];
    }
    groups[region].push(option);
    return groups;
  }, {} as Record<string, TimezoneOption[]>);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Timezone Settings">
      <div className="space-y-6">
        {/* Current Status */}
        <div className="p-4 bg-gray-800/50 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="h-5 w-5 text-blue-400" />
            <h3 className="text-white font-medium">Current Timezone</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Detected:</span>
              <span className="text-white">{currentTimezone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Effective:</span>
              <span className="text-white">{getEffectiveTimezone()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Current Time:</span>
              <span className="text-white font-mono">
                {getCurrentTime(getEffectiveTimezone())}
              </span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Search Timezones
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by city, region, or timezone..."
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Timezone Options */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Select Timezone
          </label>
          <div className="max-h-64 overflow-y-auto space-y-4 border border-gray-600 rounded-lg p-3 bg-gray-800/30">
            {Object.entries(groupedOptions).map(([region, options]) => (
              <div key={region}>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  {region}
                </h4>
                <div className="space-y-1">
                  {options.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-700/50 cursor-pointer group"
                    >
                      <input
                        type="radio"
                        name="timezone"
                        value={option.value}
                        checked={selectedTimezone === option.value}
                        onChange={(e) => setSelectedTimezone(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex-shrink-0">
                        {selectedTimezone === option.value ? (
                          <div className="w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center">
                            <Check className="h-2.5 w-2.5 text-white" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-gray-500 group-hover:border-primary-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium">
                          {option.label}
                        </div>
                        {option.offset && (
                          <div className="text-xs text-gray-400">
                            {option.offset}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-xs text-gray-500 font-mono">
                        {option.value !== 'auto' && getCurrentTime(option.value)}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        {selectedTimezone !== 'auto' && (
          <div className="p-3 bg-primary-900/20 border border-primary-500/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-primary-400" />
              <span className="text-primary-400 text-sm font-medium">Preview</span>
            </div>
            <div className="text-sm text-gray-300">
              Your selected timezone will show times like:{' '}
              <span className="font-mono text-white">
                {getCurrentTime(selectedTimezone)}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-700">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? 'Saving...' : 'Save Timezone'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}